// src/App.tsx - Consolidated dashboard routes under /dashboard/*
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LinkAgentProvider } from "@/contexts/LinkAgentProvider";
import { LinkLauncherProvider } from "@/contexts/LinkLauncherContext";
import LinkLauncher from "@/components/layout/LinkLauncher";
import EnvRibbon from "@/components/layout/EnvRibbon";
import { InvitationsLauncherProvider } from "@/contexts/InvitationsLauncherContext";
import { AppDataProvider } from "@/contexts/AppDataContext";
import { CandidateProfileProvider } from "@/contexts/CandidateProfileContext";
import { AdminProvider } from "@/contexts/AdminContext";
import { EmployerProvider } from "@/contexts/EmployerContext";
import { Toaster } from "@/components/ui/toaster";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import CONFIG from "@/lib/config";

// Pages
import Index from "@/pages/Index";
import Login from "@/pages/Login";
import AccountSettings from "@/pages/AccountSettings";
import NotFound from "@/pages/NotFound";
import AboutPage from "@/pages/AboutPage.tsx";
import TermsPage from "@/pages/legal/TermsPage.tsx";
import PrivacyPage from "@/pages/legal/PrivacyPage.tsx";
import GoogleCallback from "@/pages/auth/GoogleCallbackPage.tsx";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage.tsx";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage.tsx";
import EmailVerification from "@/pages/verify/VerifyPage.tsx";

// Layout
import { ScrollableLayout } from "@/components/layout/ScrollableLayout";

// Admin Components
import AdminHome from "@/components/dashboard/admin/AdminHome.tsx";
import AdminOrganisations from "@/components/dashboard/admin/AdminOrganisations.tsx";
import AdminUsers from "@/components/dashboard/admin/AdminUsers.tsx";
import NewOrganisationWizard from "@/components/dashboard/admin/NewOrganisationWizard.tsx";
import AdminOrganisationDetail from "@/components/dashboard/admin/OrganisationDetails.tsx";
import AdminContacts from "@/components/dashboard/admin/AdminContacts.tsx";

// Employer Components
import EmployerProfile from "@/components/dashboard/employer/profile/EmployerProfile.tsx";
import NeedDetail from "@/components/dashboard/employer/needs/NeedDetail.tsx";
import NeedCandidatePreviewPage from "@/components/dashboard/employer/needs/NeedCandidatePreviewPage.tsx";
import NeedsListView from "@/components/dashboard/employer/needs/NeedsListView.tsx";
import ProgramTab from "@/components/dashboard/employer/onboarding/ProgramTab.tsx";
import EmployerCoursesPage from "@/components/dashboard/employer/onboarding/EmployerCoursesPage.tsx";
import EmployerModuleDetailPage from "@/components/dashboard/employer/onboarding/EmployerModuleDetailPage.tsx";
import EmployerModuleDetailView from "@/components/dashboard/employer/onboarding/EmployerModuleDetailView.tsx";
import SourceLibraryPage from "@/components/dashboard/employer/library/SourceLibraryPage.tsx";
import ApplicationsPage from "@/components/dashboard/employer/applications/ApplicationsPage.tsx";
import MedarbetareArea from "@/components/dashboard/employer/employees/MedarbetareArea.tsx";
import InvitationAcceptPage from "@/pages/InvitationAcceptPage.tsx";

// Candidate Components
import CandidateHome from "@/components/dashboard/candidate/CandidateHome.tsx";
import EmployerHome from "@/components/dashboard/employer/EmployerHome.tsx";
import CandidateProfile from "@/components/dashboard/candidate/CandidateProfile.tsx";
import JobbArea from "@/components/dashboard/candidate/job-board/JobbArea.tsx";
import ReceivedInvitationsPage from "@/components/dashboard/candidate/ReceivedInvitationsPage.tsx";
import JobDetailPage from "@/components/dashboard/candidate/job-board/JobDetailPage.tsx";
import PublicJobPage from "@/components/public/PublicJobPage.tsx";
import ModulerPage from "@/components/dashboard/candidate/ModulerPage.tsx";
import MyWorkplacePage from "@/components/dashboard/candidate/MyWorkplacePage.tsx";
import {SignupJourneyProvider} from "@/contexts/SignupJourneyContext.tsx";

// Module Components
import { ModuleProgressProvider } from "@/contexts/ModuleProgressContext";
import ModuleViewer from "@/components/modules/ModuleViewer";

// Profile Setup (for candidates without a profile)
import ProfileSetupPage from "@/components/dashboard/candidate/ProfileSetupPage";
import { useCandidateProfile } from "@/contexts/CandidateProfileContext";

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: 1,
        },
    },
});

/**
 * Protected Route - Requires authentication
 */
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    const { tokens, isLoading } = useAuth();

    if (isLoading) {
        return null;
    }

    if (!tokens) {
        return <Navigate to="/" replace />;
    }

    return <>{children}</>;
};

/**
 * Guest Route - Redirects to dashboard if already authenticated
 */
const GuestRoute = ({ children }: { children: React.ReactNode }) => {
    const { tokens, isLoading } = useAuth();

    if (isLoading) {
        return null;
    }

    if (tokens) {
        return <Navigate to="/dashboard" replace />;
    }

    return <>{children}</>;
};

/**
 * DashboardShell - Unified layout shell for all dashboard routes.
 * Candidates without a profile are redirected to the full-page profile setup flow
 * (no sidebar, no dashboard access until profile is created).
 */
const DashboardShell = () => {
    const { tokens, user, isLoading, logout } = useAuth();
    const { userProfile, isLoadingProfile } = useCandidateProfile();

    if (isLoading) return null;
    if (!tokens) return <Navigate to="/" replace />;

    // Role gate — defense in depth in case a stale token lands here for a
    // role that's not in the current allowlist (e.g. candidate token after
    // the app was set to admin-only). Login.tsx is the primary gate.
    if (user && !CONFIG.ALLOWED_ROLES.has(user.role)) {
        logout();
        return <Navigate to="/" replace />;
    }

    // Candidate without a profile: show full-page setup (no sidebar).
    if (user?.role === 'candidate' && !isLoadingProfile && !userProfile) {
        return <ProfileSetupPage />;
    }

    // Still loading candidate profile: show nothing to avoid layout flash
    if (user?.role === 'candidate' && isLoadingProfile) {
        return null;
    }

    const Layout = ScrollableLayout;
    return (
        <Layout>
            <Outlet />
        </Layout>
    );
};

/**
 * CoursePlayerPage — focused, full-bleed course-taking surface. Lives OUTSIDE
 * DashboardShell (no sidebar / app chrome) so the learner can concentrate on the
 * course. Route-driven (ModuleViewer reads :moduleId from the URL), so back /
 * refresh / resume all keep working; ModuleViewer's own back arrow exits.
 */
// Full Nexilink wordmark (mark + text) — same asset the sidebar uses.
const NEXILINK_WORDMARK_SRC = 'https://cdn.builder.io/api/v1/image/assets/89bc6265b1a84995b7b85b1453a49e93/e937596a79e27aedd1ca37344ef2209cd6e8c5da2cc900e6c0f5467b6176325e?apiKey=89bc6265b1a84995b7b85b1453a49e93&';

const CoursePlayerPage = () => {
    const navigate = useNavigate();
    // Scroll-aware chrome: seamless (page-coloured) at the top, solid white bar
    // with a border + shadow once you scroll — mirrors how the course's bottom
    // action bar reads, in reverse.
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 4);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);
    return (
        <div className="min-h-screen bg-gray-50">
            <div
                className={`sticky top-0 z-10 transition-colors duration-200 ${
                    scrolled ? 'bg-white border-b border-gray-100 shadow-sm' : 'bg-gray-50/90 backdrop-blur'
                }`}
            >
                <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        title="Dina svar sparas automatiskt"
                        className="inline-flex items-center gap-2 -ml-2 px-2.5 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-200/60 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" /> Tillbaka
                    </button>
                    <img src={NEXILINK_WORDMARK_SRC} alt="Nexilink" className="h-7 w-auto object-contain opacity-90" />
                </div>
            </div>
            <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8">
                <ModuleViewer chromeless />
            </div>
        </div>
    );
};

/**
 * DashboardIndex - Renders role-appropriate dashboard
 */
const DashboardIndex = () => {
    const { user } = useAuth();

    if (user?.role === 'admin') return <AdminHome />;
    // Guided "Hem" — the state-driven next-action landing for each role (Phase 2).
    if (user?.role === 'employer') return <EmployerHome />;
    return <CandidateHome />;
};

/**
 * ProfileIndex - Renders role-appropriate profile
 */
const ProfileIndex = () => {
    const { user } = useAuth();

    if (user?.role === 'employer') return <EmployerProfile />;
    if (user?.role === 'candidate') return <CandidateProfile />;
    return <Navigate to="/dashboard" replace />;
};

/**
 * OnboardingIndex - Renders role-appropriate onboarding.
 * Admin organisation creation lives at /dashboard/organisations/new instead
 * — this index now only serves employer's module-onboarding feature.
 */
const OnboardingIndex = () => {
    const { user } = useAuth();

    if (user?.role === 'employer') return <ProgramTab />;
    return <Navigate to="/dashboard" replace />;
};

/**
 * RoleGuard - Restricts route to one or more roles
 */
const RoleGuard = ({ role, children }: { role: string | string[]; children: React.ReactNode }) => {
    const { user } = useAuth();

    const allowedRoles = Array.isArray(role) ? role : [role];
    if (!user?.role || !allowedRoles.includes(user.role)) {
        return <Navigate to="/dashboard" replace />;
    }
    return <>{children}</>;
};

/**
 * Profile Router - Redirects legacy /profile to /dashboard/profile
 */
const ProfileRouter = () => {
    const { user, isLoading } = useAuth();

    if (isLoading) {
        return null;
    }

    if (user?.role === 'admin') {
        return <Navigate to="/account-settings" replace />;
    }

    return <Navigate to="/dashboard/profile" replace />;
};

/**
 * Main App Routes
 *
 * Behavior is gated by `CONFIG.ALLOWED_ROLES`:
 *   - Empty set → landing-only mode; every auth/dashboard path redirects to "/".
 *   - Non-empty → full route tree mounted; DashboardShell role-gates per request.
 *
 * `/admin` (exact) is always reachable so admins can log in even when the
 * public app is closed. After auth, Login.tsx role-checks against ALLOWED_ROLES
 * and bounces non-allowed users.
 */
const AppRoutes = () => {
    const landingOnlyRedirect = <Navigate to="/" replace />;
    const anyLoginEnabled = CONFIG.ALLOWED_ROLES.size > 0;

    return (
        <Routes>
            {/* Public Routes — always available */}
            <Route path="/" element={<Index />} />
            <Route path="/om-oss" element={<AboutPage />} />
            {/* #324 — linked from the signup consent checkbox, so they must be
                reachable with no session. Placed above every auth route for the
                same reason. Content is the client's to write; the pages, routes
                and the consent gate are built. */}
            <Route path="/villkor" element={<TermsPage />} />
            <Route path="/integritetspolicy" element={<PrivacyPage />} />
            {/* #265 — public shareable job posting (no auth). Active postings only;
                paused/closed/removed render an "inte längre tillgänglig" state. */}
            <Route path="/jobb/:id" element={<PublicJobPage />} />
            {/* Admin login — always reachable, even in admin-only soft launches */}
            <Route path="/admin" element={<Login />} />

            {/* Password reset — PUBLIC by necessity: nobody who needs these can
                log in. /reset-password is the exact path the API mails out
                (FRONTEND_BASE_URL + "/reset-password?token=..."), so renaming it
                breaks every link already in someone's inbox. */}
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {anyLoginEnabled ? (
                <>
                    {/* OAuth Callback */}
                    <Route path={CONFIG.GOOGLE_OAUTH_CALLBACK_PATH} element={<GoogleCallback />} />

                    {/* Email Verification */}
                    <Route path="/verify-email" element={<EmailVerification />} />

                    {/* Invitation Acceptance */}
                    <Route path="/accept-invitation/:token" element={<InvitationAcceptPage />} />

                    {/* Legacy /profile route - redirects to /dashboard/profile */}
                    <Route
                        path="/profile"
                        element={
                            <ProtectedRoute>
                                <ProfileRouter />
                            </ProtectedRoute>
                        }
                    />

                    {/* Account Settings - standalone page, handles its own AuthGuard */}
                    <Route
                        path="/account-settings"
                        element={<AccountSettings />}
                    />

                    {/* ==================== CONSOLIDATED DASHBOARD ROUTES ==================== */}
                    <Route path="/dashboard" element={<DashboardShell />}>
                        <Route index element={<DashboardIndex />} />
                        <Route path="profile" element={<ProfileIndex />} />
                        <Route path="onboarding" element={<OnboardingIndex />} />
                        <Route path="courses" element={<RoleGuard role="employer"><EmployerCoursesPage /></RoleGuard>} />
                        <Route path="courses/:id" element={<RoleGuard role="employer"><EmployerModuleDetailView /></RoleGuard>} />

                        {/* Admin-only routes */}
                        <Route path="users" element={<RoleGuard role="admin"><AdminUsers /></RoleGuard>} />
                        <Route path="organisations" element={<RoleGuard role="admin"><AdminOrganisations /></RoleGuard>} />
                        {/* #358 — "Kundetablering" became "Ny organisation" and moved under
                            the resource it creates. Static segment beats the :id route below. */}
                        <Route path="organisations/new" element={<RoleGuard role="admin"><NewOrganisationWizard /></RoleGuard>} />
                        <Route path="organisations/:id" element={<RoleGuard role="admin"><AdminOrganisationDetail /></RoleGuard>} />
                        <Route path="contacts" element={<RoleGuard role="admin"><AdminContacts /></RoleGuard>} />

                        {/* Employer-only routes */}
                        <Route path="needs" element={<RoleGuard role="employer"><NeedsListView /></RoleGuard>} />
                        <Route path="needs/:id" element={<RoleGuard role="employer"><NeedDetail /></RoleGuard>} />
                        <Route path="needs/:id/applications" element={<RoleGuard role="employer"><ApplicationsPage /></RoleGuard>} />
                        <Route path="employees" element={<RoleGuard role="employer"><MedarbetareArea /></RoleGuard>} />
                        <Route path="invitations" element={<RoleGuard role="employer"><MedarbetareArea /></RoleGuard>} />
                        <Route path="onboarding/modules/:id" element={<RoleGuard role="employer"><EmployerModuleDetailPage /></RoleGuard>} />
                        <Route path="library" element={<RoleGuard role="employer"><SourceLibraryPage /></RoleGuard>} />

                        {/* Candidate job board routes */}
                        <Route path="jobs" element={<RoleGuard role="candidate"><JobbArea /></RoleGuard>} />
                        <Route path="jobs/:id" element={<RoleGuard role="candidate"><JobDetailPage /></RoleGuard>} />
                        <Route path="my-applications" element={<RoleGuard role="candidate"><JobbArea /></RoleGuard>} />
                        <Route path="bevakningar" element={<RoleGuard role="candidate"><JobbArea /></RoleGuard>} />
                        <Route path="my-invitations" element={<RoleGuard role="candidate"><ReceivedInvitationsPage /></RoleGuard>} />
                        {/* #301 — the candidate module list.
                            The rename to "Modul" left this route as `kurser` on the
                            reasoning that a URL is not vocabulary and changing it
                            breaks existing links. That was half a decision: the
                            SIDEBAR link was renamed to /dashboard/moduler, so the
                            one navigation path anybody actually uses 404'd while the
                            preserved URL sat there working and unreachable.
                            Protecting one side of a pair is worse than renaming
                            both.
                            So: the route is `moduler`, matching the label and the
                            rest of the vocabulary, and `kurser` redirects to it —
                            which honours the original bookmark concern properly
                            rather than by omission. */}
                        <Route path="moduler" element={<RoleGuard role="candidate"><ModulerPage /></RoleGuard>} />
                        <Route path="kurser" element={<Navigate to="/dashboard/moduler" replace />} />
                        <Route path="my-onboarding" element={<RoleGuard role="candidate"><ModulerPage /></RoleGuard>} />
                        <Route path="my-workplace" element={<RoleGuard role="candidate"><MyWorkplacePage /></RoleGuard>} />

                        {/* Candidate module routes */}
                        <Route path="modules" element={<RoleGuard role="candidate"><ModulerPage /></RoleGuard>} />
                    </Route>

                    {/* Course-taking lives OUTSIDE DashboardShell — a focused,
                        full-bleed player (no sidebar/app chrome). Still route-driven
                        so back / refresh / resume work. */}
                    <Route
                        path="/dashboard/modules/:moduleId"
                        element={
                            <ProtectedRoute>
                                <RoleGuard role={["candidate", "employer"]}>
                                    <CoursePlayerPage />
                                </RoleGuard>
                            </ProtectedRoute>
                        }
                    />

                    {/* Employer "Gå igenom som kandidat" — the full-bleed
                        candidate-journey preview of a posting (annons + its
                        required courses, click-through into the player). */}
                    <Route
                        path="/dashboard/needs/:id/preview"
                        element={
                            <ProtectedRoute>
                                <RoleGuard role="employer">
                                    <NeedCandidatePreviewPage />
                                </RoleGuard>
                            </ProtectedRoute>
                        }
                    />

                    {/* Backwards-compat redirects for old role-prefixed routes */}
                    <Route path="/candidate/*" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/employer/*" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/admin/*" element={<Navigate to="/dashboard" replace />} />
                </>
            ) : (
                <>
                    {/* Landing-only mode: redirect every gated path back to the landing page. */}
                    <Route path={CONFIG.GOOGLE_OAUTH_CALLBACK_PATH} element={landingOnlyRedirect} />
                    <Route path="/verify-email" element={landingOnlyRedirect} />
                    <Route path="/accept-invitation/:token" element={landingOnlyRedirect} />
                    <Route path="/profile" element={landingOnlyRedirect} />
                    <Route path="/account-settings" element={landingOnlyRedirect} />
                    <Route path="/dashboard/*" element={landingOnlyRedirect} />
                    <Route path="/candidate/*" element={landingOnlyRedirect} />
                    <Route path="/employer/*" element={landingOnlyRedirect} />
                    <Route path="/admin/*" element={landingOnlyRedirect} />
                </>
            )}

            {/* 404 - Catch all unmatched routes */}
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
};

/**
 * App with all providers
 */
const App = () => {
    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <AuthProvider>
                    <SignupJourneyProvider>  {/* ← ADD THIS */}
                        <AppDataProvider>
                            <CandidateProfileProvider>
                                <ModuleProgressProvider>
                                    <AdminProvider>
                                        <EmployerProvider>
                                            {/* App-wide so AppHeader's LinkLauncher (used by the
                                                landing page + profile setup, not just the dashboard)
                                                always has a provider. The Link overlay only mounts
                                                when an employer actually opens it.
                                                LinkAgentProvider is the PARENT: it owns the persistent
                                                Link SSE stream/session above the conditional surface,
                                                so the launcher openers + the FAB (both inside) can
                                                useLinkAgent(), and the stream survives open/close. */}
                                            <LinkAgentProvider>
                                                <LinkLauncherProvider>
                                                    <InvitationsLauncherProvider>
                                                        <AppRoutes />
                                                        {/* Viewport-fixed FAB — reachable on every
                                                            page, including headerless/full-bleed ones. */}
                                                        <LinkLauncher />
                                                        <Toaster />
                                                        {/* #76 — non-prod environment marker (dev/qa). */}
                                                        <EnvRibbon />
                                                    </InvitationsLauncherProvider>
                                                </LinkLauncherProvider>
                                            </LinkAgentProvider>
                                        </EmployerProvider>
                                    </AdminProvider>
                                </ModuleProgressProvider>
                            </CandidateProfileProvider>
                        </AppDataProvider>
                    </SignupJourneyProvider>  {/* ← AND THIS */}
                </AuthProvider>
            </BrowserRouter>
        </QueryClientProvider>
    );
};

export default App;
